import { Component, Optional } from '@angular/core';
import {
  ItemView,
  ItemViewProviderDirective,
  NtapGlobalConstants
} from '@ntap/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  first,
  map
} from 'rxjs';

import { OibService } from '@app/shared/services/oib.service';
import { OibServiceBaseComponent } from './oib-service-base.component';

const ALLOWED_VIEW_NAMES = [
  'noviRegistriranKorisnikItem',
  'agent_item',
  'arhiv_posjednik_item'
];

@Component({
  selector: 'app-oib-service-panel',
  templateUrl: './oib-service-panel.component.html',
  styleUrls: ['./oib-service-panel.component.scss']
})
export class OibServicePanelComponent extends OibServiceBaseComponent {
  private showPanelSubject = new BehaviorSubject<boolean>(false);
  showPanel$ = this.showPanelSubject.asObservable();

  constructor(
    oibService: OibService,
    @Optional()
    itemViewProviderDirective: ItemViewProviderDirective<ItemView>
  ) {
    super(oibService, itemViewProviderDirective);
  }

  protected override onInit(): void {
    if (!this.itemViewProviderDirective) {
      return;
    }

    this.itemViewProviderDirective.item$
      .pipe(
        filter((item) => !!item),
        map(() => {
          const viewName = this.itemViewProviderDirective.viewName;
          return ALLOWED_VIEW_NAMES.includes(viewName);
        }),
        distinctUntilChanged()
      )
      .subscribe((show) => {
        this.showPanelSubject.next(show);
      });
  }

  private isRegistriraniKorisnikCitaoniceView(): boolean {
    return (
      this.parrentFormData?.[NtapGlobalConstants.ATTR_$itemType] ===
      'en~registrirani_korisnik'
    );
  }

  protected override checkExistingOib(): void {
    if (!this.parrentFormData) {
      this.oib.reset();
      this.oibReadonly = false;
      this.loadingOib = false;
      return;
    }

    // Korisnik čitaonice
    if (this.isRegistriraniKorisnikCitaoniceView()) {
      const oib = this.parrentFormData['en~registrirani_korisnik~oib'];
      this.oib.reset();
      if (oib) {
        this.oib.setValue(oib);
        this.oibReadonly = true;
      } else {
        this.oibReadonly = false;
      }
      this.loadingOib = false;
      return;
    }

    // agent / arhiv_posjednik
    const agentId = this.parrentFormData['en~agent~id'];
    if (!agentId) {
      this.oib.reset();
      this.oibReadonly = false;
      this.loadingOib = false;
      return;
    }

    this.loadingOib = true;
    this.oib.reset();

    this.oibService
      .getAgentOib(agentId)
      .pipe(first())
      .subscribe({
        next: (response) => {
          if (response && response.oib) {
            this.oib.setValue(response.oib);
            this.oibReadonly = true;
          } else {
            this.oibReadonly = false;
          }
          this.loadingOib = false;
        },
        error: () => {
          this.loadingOib = false;
        }
      });
  }

  protected override getNaturalOnly(): boolean {
    return this.isRegistriraniKorisnikCitaoniceView();
  }

  protected override applyOibData(): void {
    if (this.isRegistriraniKorisnikCitaoniceView()) {
      this.applyForRegistriraniKorisnik();
    } else {
      this.applyForAgent();
    }
  }

  private applyForAgent() {
    const agentId = this.parrentFormData['en~agent~id'];

    this.oibService
      .applyForAgent(agentId, this.oib.value!)
      .pipe(first())
      .subscribe({
        next: () => {
          this.showDialog = false;
          this.itemViewProviderDirective.viewState = Object.assign(
            { ...this.itemViewProviderDirective.viewState },
            { 'en~agent~id': agentId }
          );
        },
        error: (e) => this.handleApplyError(e)
      });
  }

  private applyForRegistriraniKorisnik() {
    const korisnikId = this.parrentFormData['en~registrirani_korisnik~id'];

    this.oibService
      .applyForKorisnik(this.oib.value!, korisnikId)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.showDialog = false;
          const updatedItem = Object.assign({}, this.parrentFormData, response);
          this.itemViewProviderDirective.updateItem(updatedItem);
        },
        error: (e) => this.handleApplyError(e)
      });
  }
}
